import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { AccessUser, BackofficeUserSession } from '../../../../../core/models/system-settings/access-users.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperAccessUsersService } from '../../../../../core/mappers/system-settings/mapper-access-users.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';

@Component({
  selector: 'app-modal-user-sessions',
  standalone: true,
  imports: [CommonModule, PssConfirmModalComponent],
  templateUrl: './modal-user-sessions.component.html',
  styleUrl: './modal-user-sessions.component.css',
})
export class ModalUserSessionsComponent implements OnInit {
  @Input({ required: true }) user!: AccessUser;
  @Output() close = new EventEmitter<void>();

  sessions = signal<BackofficeUserSession[]>([]);
  isLoading = signal(false);
  isKicking = signal(false);
  isConfirmKickAllOpen = false;
  pendingKickSessionId: number | null = null;

  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperAccessUsersService);
  private toast = inject(ToastService);
  private cookieService = inject(CookieService);
  private permissionService = inject(MenuPermissionService);
  private router = inject(Router);

  get canKick(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    this.loadSessions();
  }

  onClose(): void {
    this.close.emit();
  }

  onRefresh(): void {
    this.loadSessions();
  }

  askKickOne(sessionId: number): void {
    if (!this.canKick || this.isKicking()) return;
    this.pendingKickSessionId = sessionId;
  }

  askKickAll(): void {
    if (!this.canKick || this.isKicking() || this.sessions().length === 0) return;
    this.isConfirmKickAllOpen = true;
  }

  closeConfirm(): void {
    this.isConfirmKickAllOpen = false;
    this.pendingKickSessionId = null;
  }

  confirmKickOne(): void {
    const sessionId = this.pendingKickSessionId;
    this.pendingKickSessionId = null;
    if (!sessionId) return;
    this.kickOne(sessionId);
  }

  confirmKickAll(): void {
    this.isConfirmKickAllOpen = false;
    this.kickAll();
  }

  private buildPayload() {
    return {
      userId: Number(this.cookieService.get('user_id') || 0),
      menuId: this.permissionService.getMenuId(this.router.url) || 0,
    };
  }

  private kickOne(sessionId: number): void {
    this.isKicking.set(true);
    this.apiService.kickBackofficeUserSession(sessionId, this.buildPayload()).subscribe({
      next: (res) => {
        this.isKicking.set(false);
        if (!res?.isSuccess) {
          this.toast.error(res?.message || 'เตะเครื่องไม่สำเร็จ');
          return;
        }
        this.toast.success(res?.message || 'เตะเครื่องเรียบร้อย');
        this.loadSessions();
      },
      error: () => {
        this.isKicking.set(false);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
      },
    });
  }

  private kickAll(): void {
    this.isKicking.set(true);
    this.apiService.kickAllBackofficeUserSessions(this.user.id, this.buildPayload()).subscribe({
      next: (res) => {
        this.isKicking.set(false);
        if (!res?.isSuccess) {
          this.toast.error(res?.message || 'เตะทุกเครื่องไม่สำเร็จ');
          return;
        }
        this.toast.success(res?.message || 'เตะทุกเครื่องเรียบร้อย');
        this.loadSessions();
      },
      error: () => {
        this.isKicking.set(false);
        this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
      },
    });
  }

  private loadSessions(): void {
    this.isLoading.set(true);
    this.apiService.getBackofficeUserSessions(this.user.id).subscribe({
      next: (res) => {
        const list = (res || []).map((d) => this.mapper.mapUserSessionFromDto(d));
        this.sessions.set(list);
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('ไม่สามารถโหลด session ได้');
        this.isLoading.set(false);
      },
    });
  }
}
